import { z } from "zod";

import { MUSCLE_SLUGS } from "../bodymap.js";
import { EFFORT_SCALES } from "../effortScale.js";
import { THEME_FAMILY_KEYS, THEME_MODES } from "../themes.js";
import { DEFAULT_WEEK_START_DAY } from "../weekWindow.js";

// LEGACY single-value role enum. Pre-2026-05-15 profiles carried a single
// `role` of coach/athlete/both — a fused model that didn't distinguish
// admin (product owner) from coach (someone who programs for others) and
// didn't allow orthogonal membership. Kept on the schema as an optional
// read-side fallback for rows written before the roles[] migration; new
// code should read through `resolveRoles()` rather than this field.
// Migration script `2026-05-15-seed-profile-roles.py` backfills roles[]
// on every existing profile; once the legacy field is empty across dev
// AND prod we can drop it in a follow-up ship.
export const LegacyProfileRole = z.enum(["coach", "athlete", "both"]);
export type LegacyProfileRole = z.infer<typeof LegacyProfileRole>;

// Orthogonal role kinds. A profile may hold zero, one, or all three —
// Antenore today is admin + coach + athlete on the same account. The
// three are independent:
//   - "admin"   : owns the JackedLogs product. Lists users, mutates the
//                 stock catalog, hard-deletes users. Single-digit count.
//                 Server-managed: profiles.ts PUT strips any client-sent
//                 'admin' value and re-injects based on email ∈
//                 ADMIN_EMAILS env (until the admin curl playbook lands
//                 in C1, this is the only way to grant admin).
//   - "coach"   : prepares programs/plans for other users. Read-only on
//                 athlete data per ROLES_AND_PERMISSIONS.md §Coach.
//   - "athlete" : implicit default for every user. Owns their own
//                 workouts. resolveRoles() returns ["athlete"] for a
//                 profile that has no roles[] and no legacy role.
// Source of truth + invariants in ROLES_AND_PERMISSIONS.md.
export const ProfileRole = z.enum(["admin", "coach", "athlete"]);
export type ProfileRole = z.infer<typeof ProfileRole>;

// 5-year buckets, intentionally not exact age. Bucketing keeps anonymous
// aggregate stats privacy-safe (k-anonymity stays meaningful) and avoids
// having to store DOB for "calculate age from birthday" logic. Opt-in:
// users who don't pick one stay out of any age-segmented analytics.
export const AgeBucket = z.enum([
  "under_18",
  "18-24",
  "25-29",
  "30-34",
  "35-39",
  "40-44",
  "45-49",
  "50-54",
  "55-59",
  "60-64",
  "65-69",
  "70-plus",
]);
export type AgeBucket = z.infer<typeof AgeBucket>;

// Display preference only — the storage canonical unit for any weight is
// kilograms (see packages/shared/src/units.ts). Convert at the edge, never
// store both. A user with unitsPreference="lb" still has their workouts
// persisted in kg; the UI converts on display and on input.
export const UnitsPreference = z.enum(["kg", "lb"]);
export type UnitsPreference = z.infer<typeof UnitsPreference>;

// Glucose display unit, independent of weight units. Storage is always
// canonical mg/dL (units.ts); this only picks how it's shown + entered.
// Default mmol/L — Switzerland and most of Europe read mmol/L.
export const GlucoseUnitsPreference = z.enum(["mgdl", "mmol"]);
export type GlucoseUnitsPreference = z.infer<typeof GlucoseUnitsPreference>;

// Body figure preference for the muscle map. Decoupled from sex on
// purpose — a non-binary user (or anyone) picks the figure they want
// the bodymap rendered as without having to declare gender. The mobile
// lib (`react-native-body-highlighter`) exposes both figures and we
// pass through. The web lib (`react-body-highlighter@2.0.5`) is
// male-only today; web ignores this field until a future ship swaps
// the lib or renders a custom SVG. Absent = "masculine" at the
// consumer level (no migration needed; legacy profiles read as
// undefined which resolves to masculine in the wrappers).
export const FigurePreference = z.enum(["masculine", "feminine"]);
export type FigurePreference = z.infer<typeof FigurePreference>;

// Biological sex — the input the US Navy body-fat formula needs (the
// female variant uses hip circumference, the male variant doesn't, so a
// correct estimate genuinely depends on it). Kept DELIBERATELY separate
// from `preferredFigure`: the figure is a free display choice
// (LGBT-friendly, see FigurePreference), while `sex` is only the math
// input + the value the figure picker seeds its default from. Optional —
// absent means no Navy estimate and the figure keeps its own default.
// Same special-category posture as bodyweight: never logged, never
// aggregated, wiped on account delete.
export const Sex = z.enum(["male", "female"]);
export type Sex = z.infer<typeof Sex>;

// Which effort scale the app speaks to this athlete. RIR is canonical
// internally (see effortScale.ts); this preference only changes what the
// Logger column shows and what scale a prescribed target is rendered in.
// Default RIR — more concrete for a beginner ("reps left in the tank")
// and the scale Antenore + the family are learning. Same enum+default
// pattern as UnitsPreference, so the read boundary always has a value.
export const EffortScalePreference = z.enum(EFFORT_SCALES);
export type EffortScalePreference = z.infer<typeof EffortScalePreference>;

// Theme preference — the user-selectable palette FAMILY (brand/catppuccin/…)
// plus the light/dark MODE. Family names + mode values reuse the single-source
// tuples from themes.ts, so a new palette added there validates here for free.
// Object-valued = one DDB map attribute. `.default` (not `.optional`) so the
// read boundary always has a value, exactly like unitsPreference/effortScale;
// `mode:"system"` follows the OS colour scheme. MUST be whitelisted in the
// profiles.put setIfPresent call (alwaysSet) or it silently drops on save —
// same failure mode as preferredFigure/painMap.
// Named `...Schema` (not the usual same-name convention) on purpose: the public
// `ThemePreference` TYPE lives in themes.ts (which owns the family/mode tuples),
// so exporting a same-named type here too would collide under the barrel's
// `export *`. This is only the zod validator; consumers use the themes.ts type.
export const ThemePreferenceSchema = z
  .object({
    family: z.enum(THEME_FAMILY_KEYS),
    mode: z.enum(THEME_MODES),
  })
  .default({ family: "catppuccin", mode: "system" });

export const Profile = z.object({
  id: z.string().min(1),
  name: z.string().min(1).max(80),
  // LEGACY single-value role — see LegacyProfileRole above. Optional so
  // back-compat reads succeed for profiles written before the roles[]
  // migration AND so new profiles can skip it entirely.
  role: LegacyProfileRole.optional(),
  // Orthogonal role memberships. See ProfileRole above. Use
  // resolveRoles()/hasRole() helpers rather than reading this directly so
  // the legacy fallback kicks in for un-migrated rows.
  roles: z.array(ProfileRole).optional(),
  ageBucket: AgeBucket.optional(),
  unitsPreference: UnitsPreference.default("kg"),
  // Glucose display unit (mg/dL | mmol/L), default mmol/L. Storage stays
  // canonical mg/dL; this only drives display + manual entry.
  glucoseUnit: GlucoseUnitsPreference.default("mmol"),
  // App-wide effort scale (RIR | RPE). See EffortScalePreference above.
  // Default RIR. Honoured by the Logger's single effort column; a target
  // prescribed in the other scale converts at render via effortScale.ts.
  effortScale: EffortScalePreference.default("rir"),
  // Which weekday the weekly-target window resets on (PG-2). 0=Sunday..6=Saturday
  // (JS Date.getDay()); default Monday (ISO / EU). The meter/coverage score the
  // CURRENT calendar week from this day, not a trailing 7 days. Same default-
  // not-optional pattern as unitsPreference so the read boundary always has a
  // value; weekWindowStart also defaults to Monday for any un-threaded caller.
  // Carries a value via .default → alwaysSet: MUST stay whitelisted in
  // profiles.put or it silently drops (the preferredFigure / painMap lesson).
  weekStartDay: z.number().int().min(0).max(6).default(DEFAULT_WEEK_START_DAY),
  // User-selectable theme (palette family + light/dark mode). See
  // ThemePreference above. Default {catppuccin, system}. Whitelisted
  // alwaysSet in profiles.put — do NOT drop it from the setIfPresent list.
  themePreference: ThemePreferenceSchema,
  // Currently-performed program ("active block", AP-3). The athlete may
  // FOLLOW several programs at once (ProgramHighlightCarousel), but performs
  // one block at a time; this pins which one's weekly `Program.targets` the
  // Home three-zone meter scores training against. Optional — absent ⇒ no
  // pinned block ⇒ the meter falls back to actual-only (graceful degrade).
  // Clearable: MUST be in the profiles.put setIfPresent whitelist (no
  // alwaysSet) so un-pinning REMOVEs it — same silent-drop lesson as
  // preferredFigure / painMap.
  currentProgramId: z.string().min(1).max(64).optional(),
  // Body figure for the muscle map (1.67.4). See FigurePreference
  // comment above. Optional — undefined resolves to "masculine" at
  // the consumer; no migration on existing rows.
  preferredFigure: FigurePreference.optional(),
  // Biological sex for the Body section (the Navy body-fat math + the
  // figure-picker default). See Sex above. Optional. MUST be added to the
  // profiles.put setIfPresent whitelist or it silently drops — same
  // failure mode as preferredFigure / painMap.
  sex: Sex.optional(),
  // Bodyweight (kg, canonical) is opt-in health data used to compute the
  // effective load on bodyweight exercises (chest-up, dip, push-up) and
  // to map percentage-of-MAX targets onto the bodyweight set type. Per
  // GDPR Art. 9 / nLPD Art. 5 lett. c this is a special-category field:
  // never aggregated, never logged, wiped on account delete. Empty by
  // default; users who don't enter it just keep getting raw rep counts
  // and absolute targets.
  bodyweightKg: z.number().min(20).max(300).optional(),
  bodyweightAt: z.iso.datetime().optional(),
  // Pain map (1.71.0). Sparse {muscleSlug → severity} captured via the
  // Profile pain picker. Read by the Catalog dot + Logger overlay to
  // route around exercises that would load painful muscles. Severity
  // semantics: red = avoid heavy load, yellow = caution, green = safe.
  // No medical advice intent; the user decides the load, the app just
  // makes the relevant muscles visible per exercise. Decision matrix
  // {exercise × muscle → severity} is a separate ship; this field only
  // stores the user's own pain state. Stored sparsely so unset muscles
  // round-trip as absent rather than null. Wiped on account delete.
  painMap: z
    .partialRecord(z.enum(MUSCLE_SLUGS), z.enum(["red", "yellow", "green"]))
    .optional(),
  // Versioned audit log of painMap changes. Server-managed: appended on
  // every Profile.put where the incoming painMap deep-differs from the
  // previous tail entry's map. Client never authors this field — it's
  // overwritten if sent. Drives the Progress dashboard's pain × volume
  // overlay (Tier 1): we need to know WHEN a muscle flared, not just
  // that it's currently flagged. Cap at 500 entries (multi-year coverage
  // at weekly-tweak cadence). painHistory[last].map is invariant-equal
  // to painMap by construction; if the two ever drift, painMap wins
  // (it's the live snapshot read by the Logger + Catalog painRisk).
  painHistory: z
    .array(
      z.object({
        at: z.iso.datetime(),
        map: z.partialRecord(
          z.enum(MUSCLE_SLUGS),
          z.enum(["red", "yellow", "green"]),
        ),
      }),
    )
    .max(500)
    .optional(),
  // EQ-1-GYM-COLDSTART — what the athlete has DECLARED is not at a place.
  //
  // ⛔ The asymmetry is the whole point (equipment-model.md §11.6, Antenore
  // 2026-08-17). "X is here" is DERIVED from the log and needs no field: if
  // you logged it there, it was there. "Y is NOT here" cannot be derived from
  // anything — a swap is ambiguous (machine busy, sore knee, the programme
  // said so), so reading absence out of it would mint a fact nobody stated.
  // It is declared, from the ✕ on the replace picker's current-exercise line,
  // and only ever by the athlete.
  //
  // Keyed by place AND exercise because that is the grain of the gesture: the
  // dialog lists the instances of ONE exercise and asks which of them are
  // missing HERE. `unnamed` is the virtual Default instance (EQ-1-DEFAULT) —
  // it has no slug by construction, so it cannot ride in `instances` and gets
  // its own flag. It is not a corner case: equipment-instance tags in
  // production are ZERO across 22 069 sets, so today it is the ONLY thing
  // there is to tick.
  //
  // ⚠️ NOT an inventory. §9.5.2 forbids a parallel registry that replaces the
  // derived set and silently drifts from it; this is a sparse overlay of
  // explicit exceptions ON TOP of a set that stays derived. It also never
  // hides anything: a declared-absent exercise sinks to the bottom of the
  // list marked, with a ✚ to put it back (ordina, non filtra — D4's rule).
  //
  // ⚠️ `place` is free text until EQ-1-PLACEID mints opaque ids, so renaming a
  // place today orphans its declarations. Known, accepted, written down.
  //
  // `at` carries provenance so the UI can say when it was declared rather
  // than presenting it as a fact of nature. Capped like painHistory.
  locationUnavailable: z
    .array(
      z.object({
        place: z.string().min(1),
        exercise: z.string().min(1),
        /** Named instance values (`machine-a`); absent when only the default. */
        instances: z.array(z.string()).optional(),
        /** The virtual Default instance, which has no slug to store. */
        unnamed: z.boolean().optional(),
        at: z.iso.datetime(),
      }),
    )
    .max(500)
    .optional(),
  // EQ-1-PIN-DEFAULT — which machine wins for one exercise at one place, when
  // the athlete has said so out loud (Antenore 2026-08-20: *"come può fare
  // un'atleta per forzare una o l'altra come default"*).
  //
  // ⛔ It is the DEMOTION's mirror, not its container: `locationUnavailable`
  // says what is NOT here, this says which of what IS here should stop being
  // asked about. Two separate arrays on purpose — folding them would let a
  // demotion sink a row or hide a machine as a side effect of electing a
  // different one, and those two answers must never agree by accident.
  //
  // Keyed by place for the same reason the demotion is: a machine lives
  // somewhere, so "my default" is a statement about a room. `unnamed: true`
  // pins the virtual Default (EQ-1-DEFAULT), which has no slug to store —
  // and pinning it is not a no-op: with two machines known the resolver asks
  // EVERY time, so electing the untagged one is how the athlete says
  // "stop asking, it is my usual one".
  //
  // At most one winner per (place, exercise): `instance` and `unnamed` are
  // alternatives, never both. Capped like its sibling.
  equipmentDefault: z
    .array(
      z.object({
        place: z.string().min(1),
        exercise: z.string().min(1),
        /** The elected named machine (`machine-a`). */
        instance: z.string().min(1).optional(),
        /** The virtual Default instance, elected explicitly. */
        unnamed: z.boolean().optional(),
        at: z.iso.datetime(),
      }),
    )
    .max(500)
    .optional(),
  // Global default for the rest-timer banner (1.98.x — inbox 820208da +
  // ddac68ab). When set, useRestTimer falls back to this value for any
  // exercise without its own Exercise.defaultRestSec. Authored from the
  // Settings → Rest timer card and from the in-Logger slider modal's
  // "Use as global default" checkbox. Stored in seconds; same domain as
  // Exercise.defaultRestSec (1–600s, REST_PICKER_MIN to REST_PICKER_MAX
  // on the mobile slider). Optional — unset users keep the existing
  // REST_FALLBACK_SEC = 90 behaviour.
  defaultRestSec: z.number().int().min(1).max(600).optional(),
  // Global default hold time for timed (isometric) exercises (inbox
  // 3dd20b1c). When set, the hold timer falls back to this for any timed
  // exercise without its own Exercise.defaultDurationSec. Stored in seconds;
  // same domain as Exercise.defaultDurationSec (1–3600). Optional — unset
  // users get the compile-time HOLD_FALLBACK_SEC (30s).
  defaultDurationSec: z.number().int().min(1).max(3600).optional(),
  // Optional social handle for the post-session share card footer (Phase 3
  // of the post-session feature). Free-form so the user can put "@antenore",
  // "antenore.gatta", a full URL, or whatever fits their main socials —
  // we never auto-link to a platform from the handle, just display it as
  // text on the card so the audience knows who to credit when re-sharing.
  // Stored under the same privacy posture as the other Profile fields
  // (wiped on account delete, never logged).
  socialHandle: z.string().min(1).max(50).optional(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type Profile = z.infer<typeof Profile>;

// Client → server payload for upsert. id/createdAt/updatedAt are server-set;
// the server uses the Cognito sub as id, sets createdAt on first insert,
// always refreshes updatedAt.
export const NewProfile = Profile.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type NewProfile = z.infer<typeof NewProfile>;

// Resolve the orthogonal roles[] from a profile that may still carry only
// the legacy single `role` field. Behaviour:
//   - If `roles` is present AND non-empty → return as-is (authoritative).
//   - Else map legacy role: both → [coach, athlete]; coach → [coach,
//     athlete]; athlete → [athlete].
//   - Else (no field set) → [athlete] (every user is implicitly an
//     athlete, see ProfileRole comment).
// Admin is never derived from legacy data — the only path to admin is the
// server stamping it on PUT based on email ∈ ADMIN_EMAILS. A legacy
// profile reads as non-admin until the migration script backfills.
export const resolveRoles = (
  p: Pick<Profile, "roles" | "role"> | null | undefined,
): ProfileRole[] => {
  if (!p) return ["athlete"];
  if (p.roles && p.roles.length > 0) return p.roles;
  if (p.role === "both") return ["coach", "athlete"];
  if (p.role === "coach") return ["coach", "athlete"];
  if (p.role === "athlete") return ["athlete"];
  return ["athlete"];
};

export const hasRole = (
  role: ProfileRole,
  p: Pick<Profile, "roles" | "role"> | null | undefined,
): boolean => resolveRoles(p).includes(role);
